import React from 'react';
import { motion } from 'framer-motion';
import './PrivacyPolicyPage.css';

const TermsOfServicePage = () => {
  return (
    <div className="policy-page">
      <section className="policy-hero">
        <div className="policy-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Terms of Service</h1>
            <p className="last-updated">Last Updated: November 25, 2024</p>
          </motion.div>
        </div>
      </section>

      <section className="policy-content">
        <div className="policy-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="policy-section">
              <h2>1. Agreement to Terms</h2>
              <p>
                Welcome to Dexova. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these Terms, you may not access our services.
              </p>
              <p>
                These Terms apply to all visitors, users, and others who access or use our services. Please read these Terms carefully before using our services.
              </p>
            </div>

            <div className="policy-section">
              <h2>2. Description of Services</h2>
              <p>
                Dexova provides software development, web development, mobile application development, blockchain solutions, and related technology services ("Services"). We reserve the right to modify, suspend, or discontinue any part of our Services at any time without notice.
              </p>
            </div>

            <div className="policy-section">
              <h2>3. User Accounts</h2>
              <h3>3.1 Account Creation</h3>
              <p>
                To access certain features of our Services, you may be required to create an account. You agree to:
              </p>
              <ul>
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept all responsibility for activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>

              <h3>3.2 Account Termination</h3>
              <p>
                We reserve the right to suspend or terminate your account at our sole discretion, without notice or liability, for any reason, including violation of these Terms.
              </p>
            </div>

            <div className="policy-section">
              <h2>4. Acceptable Use</h2>
              <p>You agree not to use our Services to:</p>
              <ul>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful code or malware</li>
                <li>Engage in fraudulent or deceptive practices</li>
                <li>Harass, abuse, or harm others</li>
                <li>Interfere with the proper functioning of our Services</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use our Services for any illegal or unauthorized purpose</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>5. Intellectual Property Rights</h2>
              <h3>5.1 Our Intellectual Property</h3>
              <p>
                All content, features, and functionality of our Services, including but not limited to text, graphics, logos, images, software, and code, are owned by Dexova or our licensors and are protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3>5.2 Your Content</h3>
              <p>
                You retain ownership of any content you submit to us. However, by submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content in connection with providing our Services.
              </p>

              <h3>5.3 Project Work</h3>
              <p>
                Upon full payment for services rendered, all intellectual property rights in the final deliverables will be transferred to you, unless otherwise specified in a separate agreement. We reserve the right to use project work in our portfolio and marketing materials unless confidentiality is explicitly agreed upon.
              </p>
            </div>

            <div className="policy-section">
              <h2>6. Payment Terms</h2>
              <h3>6.1 Pricing and Fees</h3>
              <p>
                You agree to pay all fees or charges to your account based on our pricing in effect at the time the charges are incurred. All fees are non-refundable unless otherwise stated.
              </p>

              <h3>6.2 Payment Methods</h3>
              <p>
                We accept various payment methods as specified on our website. You authorize us to charge your chosen payment method for all fees incurred.
              </p>

              <h3>6.3 Late Payments</h3>
              <p>
                Late payments may result in suspension of services and may incur additional fees or interest charges as permitted by law.
              </p>
            </div>

            <div className="policy-section">
              <h2>7. Service Level and Support</h2>
              <p>
                We strive to provide high-quality services and support. However, we do not guarantee uninterrupted or error-free operation of our Services. Support is provided as outlined in your service agreement or as reasonably available.
              </p>
            </div>

            <div className="policy-section">
              <h2>8. Warranties and Disclaimers</h2>
              <h3>8.1 Limited Warranty</h3>
              <p>
                We warrant that our Services will be performed in a professional and workmanlike manner in accordance with industry standards.
              </p>

              <h3>8.2 Disclaimer</h3>
              <p>
                EXCEPT AS EXPRESSLY PROVIDED, OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
            </div>

            <div className="policy-section">
              <h2>9. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, DEXOVA SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p>
                OUR TOTAL LIABILITY FOR ANY CLAIMS UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </div>

            <div className="policy-section">
              <h2>10. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Dexova and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with:
              </p>
              <ul>
                <li>Your access to or use of our Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Your content or conduct</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>11. Confidentiality</h2>
              <p>
                Both parties agree to keep confidential any proprietary or sensitive information disclosed during the course of the business relationship. This obligation survives termination of the service agreement.
              </p>
            </div>

            <div className="policy-section">
              <h2>12. Term and Termination</h2>
              <h3>12.1 Term</h3>
              <p>
                These Terms remain in effect while you use our Services.
              </p>

              <h3>12.2 Termination</h3>
              <p>
                Either party may terminate the service agreement with written notice as specified in the agreement. Upon termination:
              </p>
              <ul>
                <li>Your right to access our Services will immediately cease</li>
                <li>You remain liable for all charges incurred prior to termination</li>
                <li>Provisions that by their nature should survive termination will survive</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>13. Governing Law and Dispute Resolution</h2>
              <h3>13.1 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Dexova operates, without regard to conflict of law principles.
              </p>

              <h3>13.2 Dispute Resolution</h3>
              <p>
                Any disputes arising from these Terms or our Services shall first be attempted to be resolved through good-faith negotiations. If negotiations fail, disputes shall be resolved through binding arbitration or in the courts of our jurisdiction.
              </p>
            </div>

            <div className="policy-section">
              <h2>14. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the new Terms on our website and updating the "Last Updated" date. Your continued use of our Services after such modifications constitutes your acceptance of the updated Terms.
              </p>
            </div>

            <div className="policy-section">
              <h2>15. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </div>

            <div className="policy-section">
              <h2>16. Entire Agreement</h2>
              <p>
                These Terms, together with any service agreements and our Privacy Policy, constitute the entire agreement between you and Dexova regarding our Services and supersede all prior agreements and understandings.
              </p>
            </div>

            <div className="policy-section">
              <h2>17. Contact Information</h2>
              <p>If you have any questions about these Terms of Service, please contact us:</p>
              <ul>
                <li><strong>Email:</strong> hello.dexova@gmail.com</li>
                <li><strong>Website:</strong> <a href="https://dexova.in" target="_blank" rel="noopener noreferrer">https://dexova.in</a></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
